import {
  type BackNForthSectionProps,
  type CarouselItems,
  type ComparisonCardItemType,
  type ComparisonCardSectionType,
  type ComponentImage,
  type FeatureGridItem,
  type QuoteCtaLinkType,
  type QuoteType,
  type FAQItem,
  type ComponentCTA,
} from '@lucid/components-react';
import { getLinkPath } from '@lucid/utils-drupal';
import {
  type Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItems,
  type Drupal_ParagraphCarouselItem,
  type Drupal_ParagraphComparisonCard,
  type Drupal_ParagraphFeatureGridItem,
  type Drupal_ParagraphNewBackNForthSection,
  type Drupal_ParagraphNewBackNForthSections,
  type Drupal_FieldParagraphFaqSectionFieldFaqItem,
  type Drupal_ParagraphQuote,
  type Drupal_ParagraphSeoImage,
  type Drupal_ParagraphFaqItem,
  type Drupal_TaxonomyTermAnimations,
  type Drupal_ParagraphCtaLinkButton,
  type Drupal_NodeBlogPost,
  type Drupal_ParagraphStatBackNForthSection,
  type Drupal_ParagraphQuoteAttribution,
  type Drupal_ParagraphCompanyInfo,
} from 'generated/drupal-graphql';
import { type StatBackNForthSectionProps } from '@lucid/components-react/src/components/stat-back-n-forth/stat-back-n-forth';
import type { Lucid } from 'types/types';

export const mapFaqItems = (item: Drupal_FieldParagraphFaqSectionFieldFaqItem): FAQItem => {
  const entity = item.entity as Drupal_ParagraphFaqItem;
  const entityLinks = entity?.fieldFaqItemLink?.map(link => ({
    linkText: link.title as string,
    linkUrl: link?.url?.path as string,
  }));
  return {
    links: entityLinks,
    header: entity?.fieldHeader || '',
    description: entity?.fieldDescription,
  };
};

export const mapFeatureGridItems = (
  item: Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItems,
): FeatureGridItem => {
  const entity = item?.entity as Drupal_ParagraphFeatureGridItem;
  const image = entity.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
  const logoImage = entity.fieldLogoImage?.entity as Drupal_ParagraphSeoImage;
  return {
    description: entity.fieldSectionDescription?.processed,
    id: entity.id,
    title: entity.fieldTitle,
    image: image && {
      imageAlt: image?.fieldAltText || '',
      imageSrc: image?.fieldImageLink,
    },
    logoImage: logoImage && {
      imageAlt: logoImage?.fieldAltText || '',
      imageSrc: logoImage?.fieldImageLink,
    },
    cta: entity.fieldCta && {
      linkText: entity.fieldCta?.title,
      linkUrl: entity.fieldCta?.url?.path,
    },
  } as FeatureGridItem;
};

export const mapBackNForthSections = (backNForthData: Drupal_ParagraphNewBackNForthSections) =>
  backNForthData.fieldBackNForthSections?.reduce<BackNForthSectionProps[]>((previous, current) => {
    const currentSection = current.entity as Drupal_ParagraphNewBackNForthSection;
    const currentImage = currentSection.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        cta: {
          linkUrl: currentSection.fieldCtaLink?.url?.path as string,
          linkText: currentSection.fieldCtaLink?.title as string,
        },
        description: {
          processed: currentSection.fieldSectionDescription?.processed as string,
        },
        image: {
          imageAlt: (currentImage.fieldAltText as string) || '',
          imageSrc: currentImage.fieldImageLink as string,
        },
        title: currentSection.fieldTitle as string,
        video: currentSection.fieldVideoLink as string,
      },
    ];
  }, []) || [];

export const mapStatBackNForthSections = (backNForthData: Drupal_ParagraphStatBackNForthSection[]) =>
  backNForthData?.reduce<StatBackNForthSectionProps[]>((previous, current) => {
    const currentContentSection = current.fieldBackNForthSection?.entity as Drupal_ParagraphNewBackNForthSection;
    const currentImage = currentContentSection.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    const currentStatCta = current.fieldCtaButtonNew?.entity as Drupal_ParagraphCtaLinkButton;

    return [
      ...previous,
      {
        cta: {
          linkUrl: currentContentSection.fieldCtaLink?.url?.path as string,
          linkText: currentContentSection.fieldCtaLink?.title as string,
        },
        description: {
          processed: currentContentSection.fieldSectionDescription?.processed as string,
        },
        image: {
          imageAlt: (currentImage?.fieldAltText as string) || '',
          imageSrc: (currentImage?.fieldImageLink as string) || '',
        },
        subtitle: currentContentSection.fieldSubheading as string,
        title: currentContentSection.fieldTitle as string,
        video: currentContentSection.fieldVideoLink as string,
        stat: current.fieldStat,
        statDescription: current.fieldStatDescription,
        statCta: {
          displayAsButton: currentStatCta?.fieldDisplayAsButton,
          isSecondary: currentStatCta?.fieldIsSecondary,
          linkUrl: currentStatCta?.fieldCta?.url?.path || '',
          linkText: currentStatCta?.fieldCta?.title || '',
        },
      },
    ];
  }, []) || [];

export const mapQuoteCarouselData = (quoteCarouselItems: Drupal_ParagraphQuote[]) =>
  quoteCarouselItems?.reduce<QuoteType[]>((previous, current) => {
    const drupalQuoteCta = current.fieldQuoteCtaLink?.entity as Drupal_ParagraphCtaLinkButton;
    const fieldQuoteAuthor = current?.fieldAttributionData?.entity as Drupal_ParagraphQuoteAttribution;
    const fieldQuoteCompany = current?.fieldCompanyInfo?.entity as Drupal_ParagraphCompanyInfo;
    const fieldQuoteCompanyLogo = fieldQuoteCompany?.fieldCompanyLogo?.entity as Drupal_ParagraphSeoImage;
    const quoteCta = drupalQuoteCta.fieldCta as QuoteCtaLinkType;
    return [
      ...previous,
      {
        author: {
          firstName: fieldQuoteAuthor?.fieldFirstName,
          lastName: fieldQuoteAuthor?.fieldLastName,
          title: fieldQuoteAuthor?.fieldTitle,
        },
        company: {
          name: fieldQuoteCompany?.fieldCompanyName,
          logo: {
            imageSrc: fieldQuoteCompanyLogo?.fieldImageLink || '',
            imageAlt: fieldQuoteCompanyLogo?.fieldAltText || '',
          },
        },
        bottomAttribution: current.fieldAttribution,
        quote: current.fieldQuote,
        cta: quoteCta,
      },
    ];
  }, []) || [];

export const mapComparisonCards = (comparisonCards: Drupal_ParagraphComparisonCard[]) =>
  comparisonCards?.map(card => {
    const comparisonCardTitle = card?.fieldComparisonCardTitle;
    const comparisonSections = card?.fieldComparisonSections?.map(
      section => section.entity,
    ) as ComparisonCardSectionType[];
    const comparisonImage = card?.fieldImage?.entity as Drupal_ParagraphSeoImage;
    const comparisonImageNew = {
      imageAlt: comparisonImage?.fieldAltText,
      imageSrc: comparisonImage?.fieldImageLink,
    } as ComponentImage;
    const comparisonCta = card?.fieldCta;

    return {
      fieldComparisonCardTitle: comparisonCardTitle,
      fieldComparisonSections: comparisonSections,
      fieldCta: comparisonCta,
      fieldImage: comparisonImageNew,
    };
  }) as ComparisonCardItemType[];

export const mapCarouselItems = (carouselItems: Drupal_ParagraphCarouselItem[]) =>
  carouselItems.reduce<CarouselItems[]>((previous, current) => {
    const item = current as Drupal_ParagraphCarouselItem;
    const animationData = item.fieldAnimation?.entity as Drupal_TaxonomyTermAnimations;
    const itemImage = item.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        animation: {
          data: animationData?.fieldAnimationData,
        },
        heading: item.fieldHeading!,
        subheading: item.fieldSubtitle!,
        image: {
          imageAlt: itemImage.fieldAltText! || '',
          imageSrc: itemImage.fieldImageLink!,
        },
        footerText: item.fieldCarouselFooterText!,
      },
    ];
  }, []);

export const mapCTAs = (
  ctas: { entity: Drupal_ParagraphCtaLinkButton }[],
  languageKey: Lucid.I18n.AvailableLanguagesKey,
) =>
  ctas.reduce<ComponentCTA[]>((previous, current) => {
    const item = current.entity as Drupal_ParagraphCtaLinkButton;

    return [
      ...previous,
      {
        url: getLinkPath(item.fieldCta?.url, languageKey),
        text: item.fieldCta?.title,
        displayAsButton: item.fieldDisplayAsButton,
      },
    ];
  }, []);

export const mapPostPreviews = (posts: Drupal_NodeBlogPost[]) =>
  posts
    .filter(entity => entity)
    .map(entity => {
      const featuredImage = entity.fieldBlogFeaturedImage?.entity as Drupal_ParagraphSeoImage;
      const categories = entity.fieldCategories
        ?.filter(c => c.entity && c.entity.entityTranslation)
        .map(c => {
          const categoryEntity = c.entity!.entityTranslation!;
          const postCount = c.entity!.reverseFieldCategoriesNode.count || 0;
          return {
            text: categoryEntity.entityLabel || '',
            url: categoryEntity.entityUrl?.path || '',
            postCount,
          };
        });
      return {
        title: entity.title,
        image: featuredImage
          ? {
              alt: featuredImage.fieldAltText,
              src: featuredImage.fieldImageLink,
            }
          : undefined,
        categories,
        summary: entity.fieldHomepageSummary,
        url: entity.entityUrl?.path,
      };
    });
